/**
 * Component: Widget API Client
 * SpecReferenceID: WIDGET-V1
 * Requirements: WIDGET-V1-R06, WIDGET-V1-R07
 *
 * HTTP client that wraps fetch with three guarantees:
 *
 *   1. (R06) Every request carries the host-provided JWT
 *      as ``Authorization: Bearer <token>``. The widget
 *      never attempts to mint its own token — AUTH-V1-R04
 *      requires the host to exchange its API key for a JWT
 *      on its own backend and pass the result in.
 *
 *   2. (R07) The client never sends an API key. ``token``
 *      is the only auth material accepted; there is no
 *      code path that reads an API key from configuration
 *      or from window properties. The R07 pinning test in
 *      tests/no-api-key.test.ts asserts this
 *      invariant against the built bundles.
 *
 *   3. Network and HTTP errors are normalised into the
 *      documented ``WidgetError`` subclasses from
 *      ``./errors.ts`` so host ``onError`` callbacks
 *      receive a stable, pattern-matchable shape.
 *
 * R08 (401 retry + onAuthRefresh) is layered on top of this
 * file in the R08 commit of Batch B; this commit ships the
 * bare Bearer-injection + error-normalisation skeleton.
 */

import {
  WidgetApiError,
  WidgetAuthError,
  WidgetNetworkError,
  WidgetRateLimitError,
  parseRetryAfterHeader,
} from './errors';

/**
 * Signature of the documented host ``onAuthRefresh``
 * callback the widget invokes when a request returns 401.
 * The host exchanges its API key for a fresh JWT on its
 * own backend and returns the new token; the widget
 * updates its stored token and retries the failing
 * request exactly once.
 */
export type OnAuthRefresh = () => Promise<string>;

/**
 * Configuration for the HTTP client. ``baseUrl`` is the
 * Galen deployment root (e.g. ``https://galen-portal.example.com``);
 * the client appends ``/api/...`` paths to it. ``token`` is
 * the host-minted JWT used as the Bearer credential.
 *
 * ``onAuthRefresh`` is optional; when omitted a 401 is
 * surfaced immediately as ``WidgetAuthError``. Hosts that
 * cannot re-mint JWTs at runtime (e.g. they bake a JWT into
 * the page at render time) leave this undefined.
 */
export interface WidgetApiClientConfig {
  baseUrl: string;
  token: string;
  onAuthRefresh?: OnAuthRefresh;
}

/**
 * Subset of the WHATWG fetch options the client accepts. A
 * narrow interface keeps the public surface stable if we
 * later swap fetch for a different transport.
 */
export interface WidgetApiRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

/**
 * HTTP client the init pipeline and surface components use
 * to talk to the Galen backend. Stateful only insofar as
 * it carries the current JWT; callers replace the client
 * instance (or its token field via future refresh work)
 * rather than mutating it.
 */
export class WidgetApiClient {
  private readonly baseUrl: string;
  private token: string;
  private readonly onAuthRefresh: OnAuthRefresh | undefined;

  constructor(config: WidgetApiClientConfig) {
    // Strip trailing slash so ``${baseUrl}/api/widget/config``
    // always resolves correctly regardless of caller style.
    this.baseUrl = config.baseUrl.replace(/\/+$/, '');
    this.token = config.token;
    this.onAuthRefresh = config.onAuthRefresh;
  }

  /**
   * Replace the in-flight JWT. Used by the R08 auth-refresh
   * path in a later commit; exposed here so the R06 shape
   * is complete and tests do not need to mutate private
   * state.
   */
  setToken(token: string): void {
    this.token = token;
  }

  /**
   * Return the configured base URL. The R17 streaming path
   * in ``sse.ts`` is a separate fetch call (it cannot reuse
   * ``request`` because it needs raw ReadableStream access)
   * so it pulls the base URL and token from the client via
   * these accessors rather than duplicating the configuration
   * between the two surfaces.
   */
  getBaseUrl(): string {
    return this.baseUrl;
  }

  /**
   * Return the current JWT. Read-only accessor; callers
   * needing to rotate the token go through ``setToken``
   * so the R08 refresh-and-retry flow stays the single
   * choke-point.
   */
  getToken(): string {
    return this.token;
  }

  /**
   * GET a JSON resource. Throws ``WidgetNetworkError`` on
   * transport failures and ``WidgetApiError`` on non-2xx
   * HTTP responses.
   */
  async getJson<T>(
    path: string,
    options: Omit<WidgetApiRequestOptions, 'method' | 'body'> = {},
  ): Promise<T> {
    const response = await this.request(path, {
      method: 'GET',
      signal: options.signal,
    });
    return (await response.json()) as T;
  }

  /**
   * POST JSON and return a JSON response.
   */
  async postJson<T, B = unknown>(
    path: string,
    body: B,
    options: Omit<WidgetApiRequestOptions, 'method' | 'body'> = {},
  ): Promise<T> {
    const response = await this.request(path, {
      method: 'POST',
      body,
      signal: options.signal,
    });
    return (await response.json()) as T;
  }

  /**
   * Low-level fetch wrapper. Exposed on the class so future
   * callers needing raw Response access (e.g. SSE streaming
   * paths in Batch C) can reuse the Bearer-injection logic
   * without going through the JSON convenience methods.
   *
   * R08 401 retry: on a 401 response the client invokes
   * ``onAuthRefresh`` (when configured), updates the stored
   * token, and retries the request exactly once. A second
   * 401 (or a failed ``onAuthRefresh``) surfaces as
   * ``WidgetAuthError`` so the host can react (sign-out,
   * re-prompt login, etc.) without inspecting internal
   * state.
   */
  async request(
    path: string,
    options: WidgetApiRequestOptions = {},
  ): Promise<Response> {
    return this.performRequest(path, options, /* isRetry */ false);
  }

  private async performRequest(
    path: string,
    options: WidgetApiRequestOptions,
    isRetry: boolean,
  ): Promise<Response> {
    const url = this.resolveUrl(path);
    const method = options.method ?? 'GET';
    const headers: Record<string, string> = {
      // WIDGET-V1-R06: every backend call carries the
      // host-provided JWT as a Bearer token. Setting the
      // header here (rather than asking callers to do it)
      // means no request path can accidentally forget to
      // authenticate.
      Authorization: `Bearer ${this.token}`,
    };
    let serialisedBody: BodyInit | undefined;
    if (options.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      serialisedBody = JSON.stringify(options.body);
    }

    let response: Response;
    try {
      response = await fetch(url, {
        method,
        headers,
        body: serialisedBody,
        signal: options.signal,
      });
    } catch (e) {
      // fetch rejects for network-level failures (DNS,
      // offline, CORS rejection) and abort signals. Surface
      // those as WidgetNetworkError so host callbacks can
      // pattern-match on instanceof.
      throw new WidgetNetworkError(
        `Network request to ${url} failed: ${e instanceof Error ? e.message : String(e)}`,
        e,
      );
    }

    // WIDGET-V1-R08: 401 handling. Try onAuthRefresh once,
    // then retry the request. A second 401 or a failed
    // refresh becomes WidgetAuthError.
    if (response.status === 401 && !isRetry) {
      if (!this.onAuthRefresh) {
        const body = await response.text().catch(() => null);
        throw new WidgetAuthError(
          `Request to ${url} returned HTTP 401 and no onAuthRefresh ` +
            `callback was configured. ${body ? `Server said: ${body}` : ''}`,
        );
      }
      try {
        const newToken = await this.onAuthRefresh();
        this.token = newToken;
      } catch (e) {
        throw new WidgetAuthError(
          `onAuthRefresh threw while handling a 401 from ${url}: ` +
            `${e instanceof Error ? e.message : String(e)}`,
        );
      }
      return this.performRequest(path, options, /* isRetry */ true);
    }

    if (response.status === 401 && isRetry) {
      throw new WidgetAuthError(
        `Request to ${url} returned HTTP 401 even after onAuthRefresh; ` +
          `the refreshed JWT is still being rejected by the backend.`,
      );
    }

    // WIDGET-V1-R19: 429 responses carry a Retry-After
    // header the widget exposes to host callbacks so the
    // surface can render a "try again in N seconds" message.
    // Surfaced as a typed error distinct from WidgetApiError
    // so hosts can pattern-match on ``instanceof`` without
    // parsing status codes.
    if (response.status === 429) {
      const retryAfter = parseRetryAfterHeader(
        response.headers.get('Retry-After'),
      );
      const retryClause =
        retryAfter !== null
          ? ` Retry after ${retryAfter}s.`
          : ' Retry-After header was missing.';
      throw new WidgetRateLimitError(
        `Request to ${url} was rate-limited (HTTP 429).${retryClause}`,
        retryAfter,
      );
    }

    if (!response.ok) {
      const body = await response.text().catch(() => null);
      throw new WidgetApiError(
        `Request to ${url} returned HTTP ${response.status}`,
        response.status,
        body,
      );
    }

    return response;
  }

  /**
   * Resolve a relative path (``/api/widget/config``) to an
   * absolute URL against the configured baseUrl. Caller
   * passes the path with the leading slash so the client
   * does not have to guess whether a bare path is a path
   * or a full URL.
   */
  private resolveUrl(path: string): string {
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path;
    }
    const normalised = path.startsWith('/') ? path : `/${path}`;
    return `${this.baseUrl}${normalised}`;
  }
}
